import React, { useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Search, X, Loader2 } from 'lucide-react';

const PDFSearchPanel = ({
  showSearchPanel, setShowSearchPanel,
  searchQuery, setSearchQuery,
  searchResults, isSearching, searchError,
  handleSearch, goToSearchResult,
  continuousMode, containerRef
}) => {
  if (!showSearchPanel) return null;

  const handleResultClick = useCallback((pageNum) => {
    if (continuousMode && containerRef?.current) {
      // En modo continuo: scroll suave a la pagina
      const targetEl = containerRef.current.querySelector(`[data-page-num="${pageNum}"]`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    // Modo pagina individual
    goToSearchResult(pageNum);
  }, [continuousMode, containerRef, goToSearchResult]);

  return (
    <div className="fixed top-20 right-4 z-[60] w-[calc(100%-2rem)] max-w-sm md:max-w-md bg-slate-800 rounded-lg shadow-2xl border border-slate-700">
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Search className="h-5 w-5 text-teal-400" />
          <h3 className="font-semibold text-white">Buscar en documento</h3>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowSearchPanel(false)}
            className="ml-auto h-6 w-6 p-0 text-slate-400 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }} className="flex gap-2">
          <Input
            type="text"
            placeholder="Ej: torque, part number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
            data-testid="search-input"
          />
          <Button 
            type="submit"
            disabled={isSearching || searchQuery.length < 2}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="search-submit-btn"
          >
            {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </form>
        
        {searchError && (
          <p className="text-red-400 text-sm mt-2">{searchError}</p>
        )}
        
        {searchResults.length > 0 && (
          <div className="mt-4 max-h-64 overflow-auto">
            <p className="text-slate-400 text-sm mb-2">
              Encontrado en {searchResults.length} pagina{searchResults.length > 1 ? 's' : ''}:
            </p>
            <div className="space-y-2">
              {searchResults.map((result, idx) => (
                <button
                  key={`sr-${result.page_num}`}
                  onClick={() => handleResultClick(result.page_num)}
                  className="w-full text-left p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors"
                  data-testid={`search-result-${result.page_num}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="bg-teal-500/20 text-teal-400 px-2 py-0.5 rounded text-sm font-medium">
                      Pag. {result.page_num}
                    </span>
                    <span className="text-slate-500 text-xs">
                      {result.matches.length} coincidencia{result.matches.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="text-slate-300 text-sm line-clamp-2">
                    {result.matches[0]}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {searchResults.length === 0 && !searchError && !isSearching && searchQuery.length >= 2 && (
          <p className="text-slate-500 text-sm mt-3 text-center">
            Presiona buscar para encontrar "{searchQuery}"
          </p>
        )}
      </div>
    </div>
  );
};

export default PDFSearchPanel;
